import React, { useContext, useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PublicIcon from "@mui/icons-material/Public";

import { appointmentsApi } from "../Components/appointmentsApi";
import { buildStatusBadgeStyle, formatDateTime, statusLabel } from "../Components/appointmentsUiUtils";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { url } from "../Components/url";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import styles from "./AppointmentsDashboardPage.module.scss";

const STATUS_OPTIONS = ["booked", "cancelled", "attended", "missed"];
const endpoint = (path) => `${String(url).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;

function uniqueEnquiries(rows = []) {
  const seen = new Set();
  const out = [];
  rows.forEach((item) => {
    const id = Number(item?.id);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(item);
  });
  return out;
}

export default function AppointmentsDashboardPage() {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const { username } = useContext(UserContext);
  const { role } = useContext(RoleContext);

  const appointmentsBackPath = useMemo(() => {
    if (role === "Counsellor" || role === "Visa Counsellor" || role === "Visa&Application Counsellor") {
      return "/appointments/my";
    }
    if (role === "admin" || role === "Manager") return "/appointments/all";
    return "/appointments/calendar";
  }, [role]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailDraft, setDetailDraft] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const [saving, setSaving] = useState(false);

  const loadEnquiries = async () => {
    try {
      const buckets = [];
      try {
        const mineRes = await fetchWithAuth(
          endpoint(`/enquiry/get_new_enquiries?id=${encodeURIComponent(username || "")}&page=1&per_page=200`),
          { method: "GET", credentials: "include" }
        );
        const mineData = await mineRes.json();
        buckets.push(Array.isArray(mineData?.enquiries) ? mineData.enquiries : []);
      } catch (_error) {
        buckets.push([]);
      }
      try {
        const allRes = await fetchWithAuth(
          endpoint("/enquiry/get_all_enquiries?page=1&page_size=200&name=none&source=none&status=none"),
          { method: "GET", credentials: "include" }
        );
        const allData = await allRes.json();
        buckets.push(Array.isArray(allData?.data) ? allData.data : []);
      } catch (_error) {
        buckets.push([]);
      }
      setEnquiries(uniqueEnquiries(buckets.flat()));
    } catch (_error) {
      setEnquiries([]);
    }
  };

  const loadDetail = async () => {
    if (!appointmentId) {
      setDetail(null);
      setDetailDraft(null);
      return;
    }
    setLoading(true);
    setError("");
    const data = await appointmentsApi.getAppointment(appointmentId);
    setLoading(false);
    if (data?.error) {
      setError(data.error);
      return;
    }
    setDetail(data.appointment);
    setDetailDraft({
      enquiry_id: data.appointment.enquiry_id || "",
      customer_name: data.appointment.customer_name || "",
      customer_email: data.appointment.customer_email || "",
      customer_phone: data.appointment.customer_phone || "",
      timezone: data.appointment.timezone || "UTC",
      notes_internal: data.appointment.notes_internal || "",
      status: data.appointment.status || "booked",
    });
  };

  useEffect(() => {
    loadDetail();
  }, [appointmentId]);

  useEffect(() => {
    loadEnquiries();
  }, [username]);

  const saveDetail = async () => {
    if (!detail?.id || !detailDraft) return;
    setSaving(true);
    const data = await appointmentsApi.updateAppointment(detail.id, detailDraft);
    setSaving(false);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Appointment details updated.");
    setDetail(data.appointment);
    setDetailDraft({
      enquiry_id: data.appointment.enquiry_id || "",
      customer_name: data.appointment.customer_name || "",
      customer_email: data.appointment.customer_email || "",
      customer_phone: data.appointment.customer_phone || "",
      timezone: data.appointment.timezone || "UTC",
      notes_internal: data.appointment.notes_internal || "",
      status: data.appointment.status || "booked",
    });
  };

  const initials = useMemo(() => {
    if (!detailDraft?.customer_name) return "ME";
    const parts = detailDraft.customer_name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase() || "ME";
  }, [detailDraft?.customer_name]);

  const selectedEnquiry = useMemo(
    () => enquiries.find((item) => Number(item.id) === Number(detailDraft?.enquiry_id)) || null,
    [enquiries, detailDraft?.enquiry_id]
  );

  const saveComments = async () => {
    setSaving(true);
    const data = await appointmentsApi.updateAppointment(detail.id, {
      notes_internal: detailDraft.notes_internal,
    });
    setSaving(false);
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    toast.success("Comments updated.");
    setDetail(data.appointment);
  };

  return (
    <div className={styles.page}>
      <div className={styles.wrap}>
            <div className={styles.headerCard}>
              <div>
                <h2 className={styles.title}>Meeting Dashboard</h2>
                <p className={styles.subtitle}>Detailed appointment view with editable fields and comments.</p>
              </div>
              <div className={styles.headerActions}>
                <Button variant="outline-secondary" onClick={loadDetail}>Refresh</Button>
                {appointmentId ? (
                  <Button variant="outline-primary" onClick={() => navigate(appointmentsBackPath)}>
                    Back to appointments
                  </Button>
                ) : null}
              </div>
            </div>
            {loading ? (
              <div className={styles.card}>
                <div className={styles.loadingWrap}>
                  <Spinner animation="border" />
                </div>
              </div>
            ) : error ? (
              <div className={styles.card}>
                <div className={styles.cardBody}>
                  <p className={styles.errorText}>{error}</p>
                </div>
              </div>
            ) : detail && detailDraft ? (
              <div className={styles.card}>
                <div className={styles.cardBody}>
                  <div className={styles.detailHeader}>
                    <div>
                      <h5 className={styles.heading}>Meeting #{detail.id}</h5>
                      <small className={styles.muted}>
                        {detail.appointment_type?.name || "Appointment"} • {detail.duration_minutes || "--"} minutes
                      </small>
                    </div>
                    <Badge style={buildStatusBadgeStyle(detailDraft.status)}>{statusLabel(detailDraft.status)}</Badge>
                  </div>

                  <div className={styles.profileStrip}>
                    <div className={styles.avatar}>{initials}</div>
                    <div className={styles.meta}>
                      <div><MailOutlineIcon fontSize="small" /> {selectedEnquiry?.email || detailDraft.customer_email || "No email"}</div>
                      <div><PhoneOutlinedIcon fontSize="small" /> {selectedEnquiry?.phone_number || detailDraft.customer_phone || "No phone"}</div>
                      <div><AccessTimeIcon fontSize="small" /> {formatDateTime(detail.start_time_utc, detail.timezone)}</div>
                      <div><EventAvailableIcon fontSize="small" /> {formatDateTime(detail.end_time_utc, detail.timezone)}</div>
                      <div><PublicIcon fontSize="small" /> {detailDraft.timezone || "UTC"}</div>
                    </div>
                  </div>

                  <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key || "details")} className={styles.tabs}>
                    <Tab eventKey="details" title="Details">
                      <div className={styles.tabContent}>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Label>Enquiry</Form.Label>
                            <Form.Select
                              value={detailDraft.enquiry_id || ""}
                              onChange={(e) => {
                                const enquiry = enquiries.find((item) => Number(item.id) === Number(e.target.value));
                                const fullName = `${enquiry?.first_name || ""} ${enquiry?.last_name || ""}`.trim();
                                setDetailDraft((prev) => ({
                                  ...prev,
                                  enquiry_id: e.target.value,
                                  customer_name: fullName || prev.customer_name,
                                  customer_email: enquiry?.email || prev.customer_email,
                                  customer_phone: enquiry?.phone_number || prev.customer_phone,
                                }));
                              }}
                            >
                              <option value="">Select enquiry</option>
                              {enquiries.map((item) => (
                                <option key={item.id} value={item.id}>
                                  #{item.id} - {item.first_name || ""} {item.last_name || ""} {item.email ? `(${item.email})` : ""}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={6}>
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                              value={detailDraft.status}
                              onChange={(e) => setDetailDraft((prev) => ({ ...prev, status: e.target.value }))}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <option key={status} value={status}>{statusLabel(status)}</option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col md={6}>
                            <Form.Label>Customer Name</Form.Label>
                            <Form.Control
                              value={
                                selectedEnquiry
                                  ? `${selectedEnquiry.first_name || ""} ${selectedEnquiry.last_name || ""}`.trim() || "--"
                                  : detailDraft.customer_name || "--"
                              }
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Customer Email</Form.Label>
                            <Form.Control value={selectedEnquiry?.email || detailDraft.customer_email || "--"} disabled />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Customer Phone</Form.Label>
                            <Form.Control value={selectedEnquiry?.phone_number || detailDraft.customer_phone || "--"} disabled />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Timezone</Form.Label>
                            <Form.Control
                              value={detailDraft.timezone}
                              onChange={(e) => setDetailDraft((prev) => ({ ...prev, timezone: e.target.value }))}
                            />
                          </Col>
                          <Col md={12} className={styles.saveWrap}>
                            <Button onClick={saveDetail} disabled={saving}>
                              <EditIcon fontSize="small" />
                              <span>{saving ? "Saving..." : "Save Details"}</span>
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </Tab>
                    <Tab eventKey="comments" title="Comments">
                      <div className={styles.tabContent}>
                        <Form.Label>Internal Comments</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={7}
                          placeholder="Add internal comments and meeting notes..."
                          value={detailDraft.notes_internal}
                          onChange={(e) => setDetailDraft((prev) => ({ ...prev, notes_internal: e.target.value }))}
                        />
                        <div className={styles.helpText}>
                          Comments are internal only and are not visible to customers.
                        </div>
                        <div className={styles.saveWrap}>
                          <Button onClick={saveComments} disabled={saving}>
                            <EditIcon fontSize="small" />
                            <span>{saving ? "Saving..." : "Save Comments"}</span>
                          </Button>
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </div>
            ) : (
              <div className={styles.card}>
                <div className={styles.cardBody}>
                  <p className={styles.muted}>
                    Open a meeting from your appointments list or calendar to view this dashboard.
                  </p>
                </div>
              </div>
            )}
      </div>
    </div>
  );
}
